import { FloorBounds } from '../../hooks/useFloorBounds';
import { MinimapTransform } from './MinimapUtils';
import { LayoutElement, DxfLayer } from '../../types';
import {
  ALL_RAIL_TYPES,
  isCurveRailType,
  isConnectorRailType,
  isJunctionRailType,
  getCurveSweepAngle,
  getCurveSweepSign,
  resolveRailType,
  getCircleJunctionThroughBarAngleDeg,
  getCircleJunctionTurntableBarHalfInRing,
  getStraightRailCircleJunctionReach,
} from '../../utils/topology';
import { getConnectorShiftState } from '../../utils/connectorRail';
import type { FloorRasterDecodedTile } from '../../utils/floorRasterTypes';

interface RenderOptions {
  ctx: CanvasRenderingContext2D;
  bounds: FloorBounds;
  transform: MinimapTransform;
  polylines: number[][];
  polylineLayers: number[];
  layers: DxfLayer[];
  elements: Record<string, LayoutElement>;
  elementOrder: string[];
  darkMode: boolean;
  rasterTiles?: FloorRasterDecodedTile[];
}

export function renderMinimap({
  ctx,
  bounds,
  transform,
  polylines,
  polylineLayers,
  layers,
  elements,
  elementOrder,
  darkMode,
  rasterTiles = [],
}: RenderOptions) {
  const { scale, offsetX, offsetY } = transform;

  // Clear canvas
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  // Draw background colors based on theme
  ctx.fillStyle = darkMode ? '#0f172a' : '#ffffff';
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  // Helper to map world to minimap pixels
  const mapX = (x: number) => (x - bounds.minX) * scale + offsetX;
  const mapY = (y: number) => (y - bounds.minY) * scale + offsetY;

  // Helper to draw DXF background polylines
  const drawPolylines = () => {
    if (polylines.length === 0) return;
    ctx.lineWidth = 1.0;

    // Group drawing by color to reduce context switches
    for (let p = 0; p < polylines.length; p++) {
      const layerIdx = polylineLayers[p];
      const layer = layers[layerIdx];

      // Skip hidden layers
      if (layer && !layer.visible) continue;

      const pts = polylines[p];
      if (pts.length < 4) continue;

      const layerName = (layer?.name || '').toLowerCase();

      // Determine colors according to requirements: walls (thin gray), doors/windows (small rectangles/colored)
      if (layerName.includes('wall')) {
        ctx.strokeStyle = darkMode ? '#475569' : '#94a3b8';
      } else if (layerName.includes('door')) {
        ctx.strokeStyle = darkMode ? '#d97706' : '#f97316';
      } else if (layerName.includes('window')) {
        ctx.strokeStyle = darkMode ? '#2563eb' : '#3b82f6';
      } else {
        ctx.strokeStyle = darkMode ? '#334155' : '#cbd5e1';
      }

      ctx.beginPath();
      ctx.moveTo(mapX(pts[0]), mapY(pts[1]));
      for (let i = 2; i + 1 < pts.length; i += 2) {
        ctx.lineTo(mapX(pts[i]), mapY(pts[i + 1]));
      }
      ctx.stroke();
    }
  };

  // 1. Draw DXF background floor plan (raster tiles or polylines)
  let tilesDrawn = 0;
  if (rasterTiles && rasterTiles.length > 0) {
    for (const tile of rasterTiles) {
      if (!tile?.image || tile.image.width === 0 || tile.image.height === 0) {
        continue;
      }
      try {
        const tileBounds = tile.worldBounds;
        ctx.drawImage(
          tile.image,
          mapX(tileBounds.minX),
          mapY(tileBounds.minY),
          (tileBounds.maxX - tileBounds.minX) * scale,
          (tileBounds.maxY - tileBounds.minY) * scale
        );
        tilesDrawn++;
      } catch {
        // ImageBitmap was closed or detached by the browser during zoom/eviction
      }
    }
  }

  // Fallback to polylines if no raster tiles are available or could be rendered
  if (tilesDrawn === 0) {
    drawPolylines();
  }

  // 2. Draw user-added elements from layout editor
  elementOrder.forEach((id) => {
    const el = elements[id];
    if (!el || !el.metadata.visible) return;

    ctx.save();

    // Map position to minimap
    const mx = mapX(el.position.x);
    const my = mapY(el.position.y);
    const mw = el.dimensions.width * scale;
    const mh = el.dimensions.height * scale;

    // Translate and rotate context
    ctx.translate(mx, my);
    ctx.rotate(((el.rotation || 0) * Math.PI) / 180);

    // Styling based on element type
    if (ALL_RAIL_TYPES.has(el.type)) {
      // Draw rails as thin red paths (matches 2D canvas track color).
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 1.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      const railType = resolveRailType(el);
      if (isCurveRailType(railType)) {
        const angle = getCurveSweepAngle(railType);
        const sign = getCurveSweepSign(el);
        const radius = Math.min(el.dimensions.width, el.dimensions.height) * scale;

        ctx.beginPath();
        ctx.arc(0, 0, radius, 0, (sign * angle * Math.PI) / 180, sign < 0);
        ctx.stroke();
      } else if (isJunctionRailType(railType)) {
        if (railType === 'junction-circle') {
          const cx = mw / 2;
          const cy = mh / 2;
          const r = Math.min(mw, mh) * 0.22;
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, 2 * Math.PI);
          ctx.stroke();

          const barAngleDeg = Number.isFinite(Number(el.customProperties?.turntableAngleDeg))
            ? Number(el.customProperties?.turntableAngleDeg)
            : getCircleJunctionThroughBarAngleDeg(el, Object.values(elements));
          const barHalf = getCircleJunctionTurntableBarHalfInRing(
            el.dimensions.width,
            el.dimensions.height,
            4
          );

          const rad = (barAngleDeg * Math.PI) / 180;
          ctx.beginPath();
          const barHalfS = barHalf * scale;
          ctx.moveTo(cx - barHalfS * Math.cos(rad), cy - barHalfS * Math.sin(rad));
          ctx.lineTo(cx + barHalfS * Math.cos(rad), cy + barHalfS * Math.sin(rad));
          ctx.stroke();
        }
      } else if (isConnectorRailType(railType)) {
        const shifted = getConnectorShiftState(el) === 'shifted';
        ctx.setLineDash(shifted ? [2, 2] : []);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(mw, 0);
        ctx.stroke();
        ctx.setLineDash(shifted ? [] : [2, 2]);
        ctx.beginPath();
        ctx.moveTo(0, mh);
        ctx.lineTo(mw, mh);
        ctx.stroke();
        ctx.setLineDash([2, 2]);
        ctx.beginPath();
        ctx.moveTo(mw / 2, 0);
        ctx.lineTo(mw / 2, mh);
        ctx.stroke();
        ctx.setLineDash([]);
      } else {
        const { extendStart, extendEnd } = getStraightRailCircleJunctionReach(
          el,
          Object.values(elements),
          4
        );
        const alongX = el.dimensions.width >= el.dimensions.height;
        ctx.beginPath();
        if (alongX) {
          ctx.moveTo(-extendStart * scale, mh / 2);
          ctx.lineTo((el.dimensions.width + extendEnd) * scale, mh / 2);
        } else {
          ctx.moveTo(mw / 2, -extendStart * scale);
          ctx.lineTo(mw / 2, (el.dimensions.height + extendEnd) * scale);
        }
        ctx.stroke();
      }
    } else if (el.type === 'oht-vehicle') {
      // OHT vehicle is a small red dot
      ctx.fillStyle = '#f43f5e';
      ctx.beginPath();
      ctx.arc(mw / 2, mh / 2, Math.max(2, 3 * scale), 0, 2 * Math.PI);
      ctx.fill();
    } else if (el.type === 'circle') {
      // Circle annotation
      ctx.strokeStyle = darkMode ? '#a7f3d0' : '#059669';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(mw / 2, mh / 2, mw / 2, 0, 2 * Math.PI);
      ctx.stroke();
    } else if (el.type === 'text') {
      // Skip text details, just draw a small placeholder or nothing
    } else {
      // stocker, stb, ohb, lifter, rectangle
      ctx.fillStyle = darkMode ? 'rgba(51, 65, 85, 0.7)' : 'rgba(226, 232, 240, 0.7)';
      ctx.strokeStyle = darkMode ? '#64748b' : '#475569';
      ctx.lineWidth = 1;
      
      // Draw rounded rectangle or regular rectangle depending on type
      ctx.beginPath();
      ctx.rect(0, 0, mw, mh);
      ctx.fill();
      ctx.stroke();
    }

    ctx.restore();
  });
}
